import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { ExpandMore, Send, CheckCircle, Error, Warning } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { checkSpamContent, SpamAssassinRequest, SpamAssassinResponse } from '@lib/apis';

export default function AdminSpamChecker() {
    const { enqueueSnackbar } = useSnackbar();

    // Form state
    const [formData, setFormData] = useState<SpamAssassinRequest>({
        content: '',
        threshold: 5,
        subject: '',
        from: '',
        to: '',
        date: '',
        message_id: '',
        reply_to: '',
        cc: '',
        bcc: '',
    });

    // Results state
    const [results, setResults] = useState<SpamAssassinResponse | null>(null);

    // Mutation for spam check
    const spamCheckMutation = useMutation({
        mutationKey: ['spamCheck'],
        mutationFn: (data: SpamAssassinRequest) => checkSpamContent(data),
        onSuccess: (response) => {
            setResults(response.data);
            enqueueSnackbar('Spam check completed successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            console.error('Spam check failed:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to check spam content. Please try again.',
                { variant: 'error' }
            );
        },
    });

    const handleInputChange = (field: keyof SpamAssassinRequest) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = field === 'threshold' ? Number(event.target.value) : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = () => {
        if (!formData.content.trim()) {
            enqueueSnackbar('Email content is required', { variant: 'error' });
            return;
        }

        // Filter out empty optional fields
        const requestData: SpamAssassinRequest = {
            content: formData.content,
            threshold: formData.threshold,
        };

        // Add optional fields only if they have values
        if (formData.subject?.trim()) requestData.subject = formData.subject;
        if (formData.from?.trim()) requestData.from = formData.from;
        if (formData.to?.trim()) requestData.to = formData.to;
        if (formData.date?.trim()) requestData.date = formData.date;
        if (formData.message_id?.trim()) requestData.message_id = formData.message_id;
        if (formData.reply_to?.trim()) requestData.reply_to = formData.reply_to;
        if (formData.cc?.trim()) requestData.cc = formData.cc;
        if (formData.bcc?.trim()) requestData.bcc = formData.bcc;

        spamCheckMutation.mutate(requestData);
    };

    const handleClearForm = () => {
        setFormData({
            content: '',
            threshold: 5,
            subject: '',
            from: '',
            to: '',
            date: '',
            message_id: '',
            reply_to: '',
            cc: '',
            bcc: '',
        });
        setResults(null);
    };

    const getSpamStatusColor = (isSpam: boolean) => {
        return isSpam ? 'error' : 'success';
    };

    const getSpamStatusIcon = (isSpam: boolean) => {
        return isSpam ? <Error /> : <CheckCircle />;
    };

    return (
        <Stack direction="column" spacing={3} sx={{ pb: 4 }}>
            <Typography fontWeight="bold" variant="h5">
                SpamAssassin Email Checker
            </Typography>
            <Divider />

            <Grid container spacing={3}>
                {/* Form Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Email Content to Check
                            </Typography>

                            <Stack spacing={2}>
                                {/* Required Content Field */}
                                <TextField
                                    label="Email Content *"
                                    multiline
                                    rows={8}
                                    fullWidth
                                    value={formData.content}
                                    onChange={handleInputChange('content')}
                                    placeholder="Enter the email content you want to check for spam..."
                                    required
                                />

                                {/* Threshold Field */}
                                <TextField
                                    label="Spam Threshold"
                                    type="number"
                                    fullWidth
                                    value={formData.threshold}
                                    onChange={handleInputChange('threshold')}
                                    helperText="Emails with scores above this threshold are considered spam (default: 5)"
                                    inputProps={{ min: 0, max: 20, step: 0.1 }}
                                />

                                {/* Optional Email Headers */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography>Optional Email Headers</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            <TextField
                                                label="Subject"
                                                fullWidth
                                                value={formData.subject}
                                                onChange={handleInputChange('subject')}
                                            />
                                            <TextField
                                                label="From"
                                                fullWidth
                                                value={formData.from}
                                                onChange={handleInputChange('from')}
                                                placeholder="sender@example.com"
                                            />
                                            <TextField
                                                label="To"
                                                fullWidth
                                                value={formData.to}
                                                onChange={handleInputChange('to')}
                                                placeholder="recipient@example.com"
                                            />
                                            <TextField
                                                label="Date"
                                                fullWidth
                                                value={formData.date}
                                                onChange={handleInputChange('date')}
                                                placeholder="Mon, 29 Sep 2025 10:07:55 +0000"
                                            />
                                            <TextField
                                                label="Message ID"
                                                fullWidth
                                                value={formData.message_id}
                                                onChange={handleInputChange('message_id')}
                                            />
                                            <TextField
                                                label="Reply To"
                                                fullWidth
                                                value={formData.reply_to}
                                                onChange={handleInputChange('reply_to')}
                                            />
                                            <TextField
                                                label="CC"
                                                fullWidth
                                                value={formData.cc}
                                                onChange={handleInputChange('cc')}
                                            />
                                            <TextField
                                                label="BCC"
                                                fullWidth
                                                value={formData.bcc}
                                                onChange={handleInputChange('bcc')}
                                            />
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={handleSubmit}
                                        disabled={spamCheckMutation.isPending || !formData.content.trim()}
                                        fullWidth
                                    >
                                        {spamCheckMutation.isPending ? 'Checking...' : 'Check for Spam'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleClearForm}
                                        disabled={spamCheckMutation.isPending}
                                    >
                                        Clear
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Results Section */}
                <Grid item xs={12} md={6}>
                    {results && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Spam Check Results
                                </Typography>

                                {/* Main Result */}
                                <Alert
                                    severity={results.is_spam ? 'error' : 'success'}
                                    icon={getSpamStatusIcon(results.is_spam)}
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="h6">
                                        {results.is_spam ? 'SPAM DETECTED' : 'NOT SPAM'}
                                    </Typography>
                                    <Typography>
                                        Score: {results.spam_score} / {results.threshold_used}
                                    </Typography>
                                </Alert>

                                {/* Score Details */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Score Details:
                                    </Typography>
                                    <Chip
                                        label={`Spam Score: ${results.spam_score}`}
                                        color={getSpamStatusColor(results.is_spam)}
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                    <Chip
                                        label={`Threshold: ${results.threshold_used}`}
                                        variant="outlined"
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                </Box>

                                {/* Tests Triggered */}
                                {results.parsed_output.spam_info.tests.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Tests Triggered:
                                        </Typography>
                                        <Box>
                                            {results.parsed_output.spam_info.tests.map((test, index) => (
                                                <Chip
                                                    key={index}
                                                    label={test}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Raw Output Accordion */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography>Raw SpamAssassin Output</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Paper sx={{
                                            p: 2,
                                            bgcolor: 'grey.900',
                                            border: '1px solid',
                                            borderColor: 'grey.300'
                                        }}>
                                            <Typography
                                                component="pre"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.875rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    color: 'grey.100',
                                                    margin: 0,
                                                }}
                                            >
                                                {results.parsed_output.raw_output}
                                            </Typography>
                                        </Paper>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    )}

                    {!results && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary" align="center">
                                    Enter email content and click "Check for Spam" to see results
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </Stack>
    );
}
